import React, { useState, useMemo, useCallback } from 'react';
import { Card, Button, Spinner, Alert, Form, Badge } from 'react-bootstrap';

import { useLanguage } from '../../API/Context/LanguageContext';
import { useUser } from '../../API/Context/UserContext';
import { useSlotPicker } from '../../API/Hooks/useSlotPicker';
import type { ProcessedDayColumn } from '../../API/Hooks/useSlotPicker';
import VocabCalendarWidget from '../../ConfigFiles/multilingual/VocabCalendarWidget';

import type { MatchingUser } from '../../Types/schema-helpers';
import type { TimeSlot } from '../../Utils/scheduleUtils';
import {
  toISODateString,
  parseISODate,
  formatInterval,
} from '../../Utils/scheduleUtils';
import './TimeSlotPicker.css';

interface CalendarWidgetProps {
  profile: MatchingUser | null;
  onSelectSlot?: (slot: TimeSlot) => void;
}

interface OfficeInterval {
  start: number;
  end: number;
}

const SlotColumn = React.memo<{
  col: ProcessedDayColumn;
  selectedSlotId: string | null;
  locale: string;
  onSlotClick: (slot: TimeSlot) => void;
}>(({ col, selectedSlotId, locale, onSlotClick }) => {
  return (
    <div
      className="day-column flex-shrink-0 text-center px-1"
      style={{ minWidth: '110px', maxWidth: '130px' }}
    >
      <div className={`day-column-header mb-2 py-1 rounded ${col.isToday ? 'is-today bg-primary-subtle' : 'bg-light'}`}>
        <small className="d-block text-uppercase text-muted fw-bold" style={{ fontSize: '0.7rem' }}>
          {col.dayName}
        </small>
        <span className="fw-bold fs-6 d-block leading-none">{col.dayNumber}</span>
        <small className="text-muted" style={{ fontSize: '0.65rem' }}>
          {col.monthName}
        </small>
      </div>

      <div
        className="slots-scroll-container d-flex flex-column gap-1 pe-1"
        style={{ maxHeight: '320px', overflowY: 'auto' }}
      >
        {col.generatedSlots.length === 0 ? (
          <span className="text-muted my-3" style={{ fontSize: '0.75rem' }}>
            &mdash;
          </span>
        ) : (
          col.generatedSlots.map((slot) => {
            const isBusy = slot.status === 'BUSY';
            const isSelected = selectedSlotId === slot.id;
            const timeStr = slot.start.toLocaleTimeString(locale, {
              hour: '2-digit',
              minute: '2-digit',
            });

            return (
              <Button
                key={slot.id}
                variant={isSelected ? 'primary' : isBusy ? 'light' : 'outline-primary'}
                size="sm"
                disabled={isBusy}
                className={`slot-btn w-100 ${isBusy ? 'disabled-slot' : ''}`}
                onClick={() => onSlotClick(slot)}
              >
                {timeStr}
              </Button>
            );
          })
        )}
      </div>
    </div>
  );
});

SlotColumn.displayName = 'SlotColumn';

const CalendarWidget: React.FC<CalendarWidgetProps> = ({ profile, onSelectSlot }) => {
  const { lan } = useLanguage();
  const { user } = useUser();
  const isAuthenticated = Boolean(user);

  const [selectedSlotId, setSelectedSlotId] = useState<string | null>(null);

  const maxMonths = 3;
  const locale = lan === 0 ? 'es-MX' : 'en-US';

  const {
    baseDate,
    daysColumns,
    loading,
    error,
    isPrevDisabled,
    isNextDisabled,
    handleNextDays,
    handlePrevDays,
    handleSelectSpecificDate,
  } = useSlotPicker(
    profile?.username,
    profile?.earliest_availability,
    profile?.schedule,
    maxMonths,
    locale,
    true // Hide non-working days
  );

  const daysOfWeek = useMemo(() => ['0', '1', '2', '3', '4', '5', '6'], []);

  const handleSlotClick = useCallback(
    (slot: TimeSlot) => {
      if (slot.status === 'BUSY') return;
      setSelectedSlotId(slot.id);
      if (onSelectSlot) onSelectSlot(slot);
    },
    [onSelectSlot]
  );

  const { minSelectableDateIso, maxSelectableDateIso } = useMemo(() => {
    const today = new Date();
    const maxDate = new Date();
    maxDate.setMonth(today.getMonth() + maxMonths);
    return {
      minSelectableDateIso: toISODateString(today),
      maxSelectableDateIso: toISODateString(maxDate),
    };
  }, [maxMonths]);

  const firstDay = daysColumns[0]?.date || baseDate;
  const lastDay = daysColumns[daysColumns.length - 1]?.date || baseDate;

  const { earliestDateObj, formattedEarliestDate } = useMemo(() => {
    const rawEarliest = profile?.earliest_availability;
    const dateObj = rawEarliest
      ? typeof rawEarliest === 'string'
        ? parseISODate(rawEarliest)
        : new Date(rawEarliest)
      : null;

    const formatted = dateObj
      ? dateObj.toLocaleDateString(locale, {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
        })
      : null;

    return { earliestDateObj: dateObj, formattedEarliestDate: formatted };
  }, [profile?.earliest_availability, locale]);

  const handleGoToEarliest = useCallback(() => {
    if (earliestDateObj) {
      handleSelectSpecificDate(earliestDateObj);
    }
  }, [earliestDateObj, handleSelectSpecificDate]);

  if (!profile) {
    return (
      <Card className="shadow-sm border-0">
        <Card.Header className="bg-light fw-bold py-2">
          {VocabCalendarWidget.header_title[lan]}
        </Card.Header>
        <Card.Body className="text-muted text-center py-4">
          {VocabCalendarWidget.no_profile_selected[lan]}
        </Card.Body>
      </Card>
    );
  }

  if (profile.schedule && !profile.schedule.manage_agenda) {
    const officeHours: Record<string, OfficeInterval[]> = profile.schedule.office_hours || {};

    return (
      <Card className="shadow-sm border-0 mb-3">
        <Card.Header className="bg-white border-bottom py-3">
          <h6 className="mb-0 fw-bold text-truncate">{profile.full_name}</h6>
        </Card.Header>
        <Card.Body className="p-3">
          <Alert variant="info" className="mb-3 py-2 small text-center">
            {lan === 0
              ? 'Este especialista no administra citas en línea. Consulta su horario de atención y contáctalo directamente para agendar.'
              : 'This specialist does not accept online bookings. Check their office hours below and contact them directly to schedule an appointment.'}
          </Alert>

          <div>
            <small className="text-muted fw-bold d-block mb-2 text-uppercase" style={{ fontSize: '0.75rem' }}>
              {lan === 0 ? 'Horario de Atención:' : 'Office Hours:'}
            </small>

            <div className="d-flex flex-column gap-2">
              {daysOfWeek.map((dayKey) => {
                const intervals: OfficeInterval[] = officeHours[dayKey] || [];
                const localizedDay = new Date(2026, 0, Number(dayKey) + 4).toLocaleDateString(
                  locale,
                  { weekday: 'long' }
                );

                return (
                  <div
                    key={dayKey}
                    className="p-2 rounded bg-light border-start border-3 border-primary-subtle d-flex justify-content-between align-items-center"
                  >
                    <span className="fw-bold text-capitalize text-dark me-2" style={{ fontSize: '0.85rem' }}>
                      {localizedDay}
                    </span>

                    <div className="d-flex flex-wrap gap-1 justify-content-end">
                      {intervals.length === 0 ? (
                        <span className="text-muted fst-italic" style={{ fontSize: '0.8rem' }}>
                          {lan === 0 ? 'Cerrado' : 'Closed'}
                        </span>
                      ) : (
                        intervals.map((interval: OfficeInterval, idx: number) => (
                          <Badge
                            key={idx}
                            bg="white"
                            text="dark"
                            className="border fw-normal py-1 px-2 shadow-sm"
                            style={{ fontSize: '0.8rem' }}
                          >
                            {formatInterval(interval.start, interval.end, locale)}
                          </Badge>
                        ))
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="shadow-sm border-0 slot-picker-container mb-3">
      <Card.Header className="bg-white border-bottom py-3">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h6 className="mb-0 fw-bold text-truncate" style={{ maxWidth: '180px' }}>
            {profile.full_name}
          </h6>

          {isAuthenticated && (
            <Form.Control
              type="date"
              size="sm"
              style={{ width: '130px' }}
              min={minSelectableDateIso}
              max={maxSelectableDateIso}
              value={toISODateString(baseDate)}
              onChange={(e) => {
                if (e.target.value) {
                  handleSelectSpecificDate(new Date(`${e.target.value}T00:00:00`));
                }
              }}
            />
          )}
        </div>

        {isAuthenticated && (
          <div className="d-flex justify-content-between align-items-center pt-2 border-top">
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={isPrevDisabled || loading}
              onClick={handlePrevDays}
              className="py-0 px-2"
            >
              &lsaquo; {VocabCalendarWidget.prev_button[lan]}
            </Button>

            <div className="text-center">
              <small className="text-muted fw-bold d-block leading-tight">
                {firstDay.toLocaleDateString(locale, { month: 'short', day: 'numeric' })} -{' '}
                {lastDay.toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric' })}
              </small>

              {formattedEarliestDate && (
                <Badge
                  bg="success-subtle"
                  text="success"
                  className="border border-success-subtle fw-normal mt-1 role-button"
                  style={{ cursor: 'pointer' }}
                  onClick={handleGoToEarliest}
                  title={lan === 0 ? 'Ir a la próxima cita disponible' : 'Jump to next available slot'}
                >
                  {lan === 0 ? 'Próxima disponible:' : 'Next available:'} {formattedEarliestDate}
                </Badge>
              )}
            </div>

            <Button
              variant="outline-secondary"
              size="sm"
              disabled={isNextDisabled || loading}
              onClick={handleNextDays}
              className="py-0 px-2"
            >
              {VocabCalendarWidget.next_button[lan]} &rsaquo;
            </Button>
          </div>
        )}
      </Card.Header>

      <Card.Body className="p-2">
        {!isAuthenticated ? (
          <Alert variant="info" className="mb-0 text-center py-3">
            {VocabCalendarWidget.unauthenticated_notice[lan]}
          </Alert>
        ) : loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" variant="primary" />
            <span className="ms-2 small text-muted">
              {VocabCalendarWidget.loading_schedule[lan]}
            </span>
          </div>
        ) : error ? (
          <Alert variant="warning" className="mb-0 py-2 small text-center">
            {VocabCalendarWidget.fetch_error[lan]}
          </Alert>
        ) : daysColumns.length === 0 ? (
          <Alert variant="light" className="mb-0 text-center py-3 text-muted border">
            {lan === 0 ? 'No hay días laborables esta semana.' : 'No available working days this week.'}
          </Alert>
        ) : (
          <div className="d-flex overflow-x-auto gap-2 pb-2 scrollbar-thin">
            {daysColumns.map((col) => (
              <SlotColumn
                key={col.isoDate}
                col={col}
                selectedSlotId={selectedSlotId}
                locale={locale}
                onSlotClick={handleSlotClick}
              />
            ))}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default CalendarWidget;